<template>
  <div class="advanced-filters">
    <div class="loader-wrapper" v-if="!initialized">
      <loader :loading="!initialized"></loader>
    </div>
    <div v-show="initialized">
      <button
        type="button"
        class="button muted tiny rounded clear-all"
        :class="{ hidden: selectedItems.length === 0 }"
        @click="resetAll"
      >
        <svg-icon id="cross"></svg-icon>
        <span>Clear all</span>
      </button>
      <div class="form-group vanilla">
        <label class="checkbox-group-heading">Suitable for</label>
        <expandy-children child-selector=".column" :rows="50">
          <div class="checkbox-group columns-4">
            <div class="column" v-for="option in tagData.availability_types">
              <checkbox
                :label="option.label"
                :input-value="option.id"
                v-model="settings.availabilityTypes"
              ></checkbox>
            </div>
          </div>
        </expandy-children>
      </div>
      <div class="form-group vanilla">
        <label class="checkbox-group-heading">Day Rate</label>
        <div class="group columns-2">
          <div class="column">
            <label class="form-label">From</label>
            <input type="number" v-model="settings.rateDayFrom" />
          </div>
          <div class="column">
            <label class="form-label">To</label>
            <input type="number" v-model="settings.rateDayTo" />
          </div>
        </div>
      </div>
      <div class="form-group vanilla" v-if="tagData.capacities.length">
        <label class="checkbox-group-heading">Capacity</label>
        <select-wrapper
          name="capacity"
          :options="tagData.capacities"
          v-model="settings.capacity"
        ></select-wrapper>
      </div>
      <div class="form-group vanilla">
        <label class="checkbox-group-heading">Property Type</label>
        <expandy-children child-selector=".column" :rows="50">
          <div class="checkbox-group columns-4">
            <div class="column" v-for="option in tagData.property_types">
              <checkbox
                :label="option.label"
                :input-value="option.id"
                v-model="settings.propertyTypes"
              ></checkbox>
            </div>
          </div>
        </expandy-children>
      </div>
      <div class="form-group vanilla">
        <label class="checkbox-group-heading">Features</label>
        <expandy-children child-selector=".column" :rows="50">
          <div class="checkbox-group columns-4">
            <template v-for="option in tagData.space_types">
              <div class="column" :key="option.id" v-if="option.is_countable">
                <label class="font-base option">{{ option.label }}</label>
                <div class="form-group-meta-count float-right mr-4">
                  <button
                    type="button"
                    class="circle-badge small"
                    @click="decrement(option.id)"
                  >
                    －
                  </button>
                  <button
                    type="button"
                    class="circle-badge small"
                    @click="increment(option.id)"
                  >
                    ＋
                  </button>
                  <input
                    type="number"
                    pattern="^[-/d]/d*$"
                    onkeydown="if(event.key==='.'){event.preventDefault();}"
                    oninput="event.target.value = event.target.value.replace(/[^0-9]*/g,'');"
                    v-model="settings.metaCounts.spaceTypes[option.id]"
                  />
                </div>
              </div>
              <div class="column" :key="option.id" v-else>
                <label class="font-base option" :for="'space_type_' + option.id">{{
                  option.label
                }}</label>
                <div class="form-group-meta-count sm float-right mr-1">
                  <checkbox
                    :input-value="option.id"
                    v-model="settings.spaceTypes"
                  ></checkbox>
                </div>
              </div>
            </template>
          </div>
        </expandy-children>
      </div>
      <div class="form-group submit no-divider">
        <button type="button" class="button primary" @click="applyFilters">
          Apply Settings
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import cloneDeep from "lodash/cloneDeep";

import BaseModal from "../Base";

export default {
  extends: BaseModal,

  props: {
    data: {
      type: Object,
    },
  },

  data() {
    let settings = {
      availabilityTypes: [],
      propertyTypes: [],
      spaceTypes: [],
      rateDayFrom: null,
      rateDayTo: null,
      capacity: null,
      metaCounts: {
        spaceTypes: {},
      },
    };

    if (this.data) {
      if (this.data.availabilityTypes.length) {
        settings.availabilityTypes = this.data.availabilityTypes;
      }

      if (this.data.propertyTypes.length) {
        settings.propertyTypes = this.data.propertyTypes;
      }

      if (this.data.spaceTypes.length) {
        settings.spaceTypes = this.data.spaceTypes;
      }

      if (this.data.rateDayFrom) {
        settings.rateDayFrom = +this.data.rateDayFrom;
      } 

      if (this.data.rateDayTo) {
        settings.rateDayTo = +this.data.rateDayTo;
      }
      
      if (this.data.capacity) {
        settings.capacity = +this.data.capacity;
      }

      if (this.data.metaCounts) {
        settings.metaCounts = this.data.metaCounts;
      }
    }

    return {
      settings: settings,
      initialized: false,
      tagData: {},
    };
  },

  computed: {
    selectedItems() {
      let ids = [];

      for (let prop in this.settings) {
        ids = ids.concat(this.settings[prop]);
      }

      return ids;
    },
  },

  created() {},

  mounted() {
    //TODO show user when we are loading
    axios.get("/api/form-meta/listing").then((response) => {
      this.tagData = response.data;
      this.initialized = true;
      
      this.tagData.space_types.forEach((item) => {
        if (this.settings.metaCounts.spaceTypes[item.id] == undefined) {
          this.settings.metaCounts.spaceTypes[item.id] = 0;
        }
      });
    });
  },

  methods: {
    decrement(option_id) {
      // Decrement space types meta counts
      const metaCountsSpaceTypes = cloneDeep(
        this.settings.metaCounts.spaceTypes
      );
      if (metaCountsSpaceTypes[option_id] >= 1) {
        metaCountsSpaceTypes[option_id]--;
      }
      this.settings.metaCounts.spaceTypes = metaCountsSpaceTypes;

      // Remove element from space types array
      if (this.settings.metaCounts.spaceTypes[option_id] == 0) {
        if (this.settings.spaceTypes.indexOf(option_id) !== -1) {
          const index = this.settings.spaceTypes.indexOf(option_id);
          this.settings.spaceTypes.splice(index, 1);
        }
      }
    },

    increment(option_id) {
      // Increment space types meta counts
      const metaCountsSpaceTypes = cloneDeep(
        this.settings.metaCounts.spaceTypes
      );
      metaCountsSpaceTypes[option_id]++;
      this.settings.metaCounts.spaceTypes = metaCountsSpaceTypes;

      // Add element to space types array
      if (this.settings.spaceTypes.indexOf(option_id) === -1) {
        this.settings.spaceTypes.push(option_id);
      }
    },

    applyFilters() {
      this.settings.metaCounts.spaceTypes = Object.fromEntries(
        Object.entries(this.settings.metaCounts.spaceTypes).filter(
          ([, v]) => v !== 0
        )
      )
      
      if (Object.keys(this.settings.metaCounts.spaceTypes).length) {
        this.settings.metaCounts = this.settings.metaCounts;
      } else {
        this.settings.metaCounts = null;
      }
      
      this.$emit("confirm", this.settings);
    },

    resetAll() {
      for (let prop in this.settings) {
        this.settings[prop] = [];
      }
    },

    toggleOption(value, dst) {
      let existing_idx = dst.indexOf(value);

      if (existing_idx !== -1) {
        dst.splice(existing_idx, 1);
      } else {
        dst.push(value);
      }
    },
  },
};
</script>
